/**
 * Display helpers for the server-parsed user agent.
 *
 * The backend (`pkg/useragent`) parses the raw User-Agent once and returns this
 * shape alongside the raw string on any response that exposes a user agent
 * (session history, session slots, agentic access, grant history). These pure
 * functions map the parsed fields to Font Awesome icon tuples so the SPA can
 * render a browser/OS/device glyph next to the summary. String values mirror the
 * medama-io/go-useragent constants (e.g. "iOS", "ChromeOS"), except "MacOS" which
 * the backend normalises to Apple's own casing "macOS" (see pkg/useragent).
 */

export interface ParsedUserAgent {
  Browser: string;
  BrowserVersion: string;
  OS: string;
  DeviceType: string;
  IsBot: boolean;
  Summary: string;
}

/** Font Awesome icon tuple, e.g. ["fab", "chrome"]. */
export type FaIcon = [string, string];

/** Icon for a parsed browser name; a neutral globe when unknown. */
export function browserIcon(browser: string): FaIcon {
  switch (browser) {
    case "Chrome":
      return ["fab", "chrome"];
    case "Firefox":
      return ["fab", "firefox-browser"];
    case "Safari":
      return ["fab", "safari"];
    case "Edge":
      return ["fab", "edge"];
    case "Opera":
    case "Opera Mini":
      return ["fab", "opera"];
    case "IE":
      return ["fab", "internet-explorer"];
    default:
      return ["fass", "globe"];
  }
}

/** Icon for a parsed OS name; a neutral desktop when unknown. */
export function osIcon(os: string): FaIcon {
  switch (os) {
    case "Windows":
      return ["fab", "windows"];
    case "macOS":
    case "iOS":
      return ["fab", "apple"];
    case "Android":
      return ["fab", "android"];
    case "Linux":
    case "FreeBSD":
    case "OpenBSD":
      return ["fab", "linux"];
    case "ChromeOS":
      return ["fab", "chrome"];
    default:
      return ["fass", "desktop"];
  }
}

/** Icon for a parsed device type; a desktop when unknown. */
export function deviceIcon(deviceType: string): FaIcon {
  switch (deviceType) {
    case "Mobile":
      return ["fass", "mobile-screen"];
    case "Tablet":
      return ["fass", "tablet-screen-button"];
    case "TV":
      return ["fass", "tv"];
    case "Bot":
      return ["fass", "robot"];
    default:
      return ["fass", "desktop"];
  }
}
